import React, { useState, useMemo, useCallback, useEffect } from 'react';
import classNames from 'classnames/bind';
import { Route, Switch, Redirect, useHistory } from 'react-router-dom';
import { useDispatch, shallowEqual, useSelector } from 'react-redux';

// Components
import { Button, Dropdown } from '@beef/ui-kit';
import { DataBlocks, NewTable, NewErrorBlock } from 'components';
import { ANALYTICS_DETAIL } from 'constants/detalization';
import { openPopup } from 'actions';
import { MODAL_NAMES } from 'constants/modal';
import { pushDetalizationAnalytics } from 'utils/analytics/detalization';
import { useIsMobileWidth } from 'utils/hooks/useIsMobile';
import { SectionWrapper } from '../../components/SectionWrapper';
import { EXPENSE_PAGE } from '../../components/mock';
import AccumulatorPage from '../../components/AccumulatorPage';
import ExpensePage from '../../components/ExpensePage';
import Heading from '../../components/Heading';
import CtnBlock from '../../components/CtnBlocks';

// Action
import { fetchExpenses, setTableFilters } from '../../store/expensesData/action';
import { fetchPayments } from '../../store/paymentsData/action';

// Store
import { TABLE_TYPES, TABLE_CONFIGS } from './TableConfig';
import { setDetalizationPageCurrentModal } from '../../store/actions';

// Styles
import styles from './styles.pcss';

const cx = classNames.bind(styles);

const PrepaidDetalization = ({ match }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    isExpensesFetching,
    structure,
    callDetails,
    payments,
    activeTabs,
    isRegularDetalizationReport,
    expensesError,
  } = useSelector(({ detalizationNew }) => ({
    isExpensesFetching: detalizationNew.expenses.isFetching,
    expensesError: detalizationNew.expenses.error,
    structure: detalizationNew.expenses.structure,
    callDetails: detalizationNew.expenses.callDetails,
    activeTabs: detalizationNew.expenses.activeTabs,
    payments: detalizationNew.payments,
    isRegularDetalizationReport: detalizationNew.regularDetalizationReport.numbers.length > 0,
    isRegularDetalizationReportFetching: detalizationNew.regularDetalizationReport.isFetching,
  }), shallowEqual);

  const { path } = match;
  const indexUrl = path;
  const smsUrl = `${path}/sms`;
  const callUrl = `${path}/call`;
  const dataUrl = `${path}/data`;

  const smsUrlRoaming = `${path}/roaming/sms`;
  const callUrlRoaming = `${path}/roaming/call`;
  const dataUrlRoaming = `${path}/roaming/data`;

  const [selectedDropdown, setSelectedDropdown] = useState(null);
  const [dropDownList, setDropdownList] = useState([]);

  const isMobile = useIsMobileWidth();

  useEffect(() => {
    if (!callDetails?.dropdown) return;

    const dropdownBalance = Object.entries(callDetails?.dropdown ?? {}).map(([key, value]) => ({ slug: key, label: value }));
    if (selectedDropdown === null) setSelectedDropdown(dropdownBalance[0]?.slug);
    setDropdownList(dropdownBalance);
  }, [callDetails, activeTabs, isExpensesFetching]);

  const onHistory = (ev, method) => {
    if (method === 'PUSH') setSelectedDropdown(null);
  };
  useEffect(() => history.listen(onHistory), []);

  useEffect(() => {
    if (activeTabs === 'replenish') {
      dispatch(setTableFilters('DebitBalance', 'empty'));
      dispatch(setTableFilters('Accumulators', 'empty'));
    }
    if (dropDownList.length > 0) setSelectedDropdown(dropDownList[0]?.slug);
  }, [activeTabs]);

  useEffect(() => {
    dispatch(fetchExpenses());
    dispatch(fetchPayments());

    pushDetalizationAnalytics(ANALYTICS_DETAIL.OPEN_SPEND_PAGE);
  }, []);

  const handleChangeFilterDebitBalance = (value) => {
    setSelectedDropdown(value);
    const action = value === 'empty' ? 'empty' : (x) => x?.balances?.[0]?.nameCode === value;
    dispatch(setTableFilters('DebitBalance', action));
  };

  const onOpenDetalizationReportModal = useCallback(() => {
    dispatch(openPopup(MODAL_NAMES.DETALIZATION_REPORT));
    dispatch(setDetalizationPageCurrentModal(MODAL_NAMES.DETALIZATION_REPORT));
  }, [dispatch]);

  const onOpenRegularDetalizationReportModal = useCallback(() => {
    dispatch(openPopup(MODAL_NAMES.REGULAR_DETALIZATION_REPORT));
    dispatch(setDetalizationPageCurrentModal(MODAL_NAMES.REGULAR_DETALIZATION_REPORT));
  }, [dispatch]);

  const isAnyFetching = isExpensesFetching || payments?.isFetching;

  const dataTable = useMemo(() => {
    const defReturn = { tableBody: [], mobileColumns: [], desktopColumns: [], dateGetter: undefined };
    if (isAnyFetching) return defReturn;
    if (!activeTabs) return defReturn;
    let tableBody = null;
    let tableType = null;
    let textError = null;
    switch (activeTabs) {
      case 'debit':
        tableBody = callDetails.debit;
        tableType = TABLE_TYPES.EXPENSES;
        textError = 'За этот период нет списаний. Выберите другой период.';
        break;
      case 'replenish':
        tableBody = payments?.list?.detalizationPayments;
        tableType = TABLE_TYPES.PAYMENTS;
        textError = 'За этот период нет пополнений. Выберите другой период.';
        break;
      default:
        throw new Error(`Unknown selected tab: ${activeTabs}`);
    }
    const tableConfig = TABLE_CONFIGS[tableType];
    return { ...tableConfig, tableBody, textError };
  }, [isExpensesFetching, structure, activeTabs, payments]);

  if (expensesError) {
    return (
      <DataBlocks.Container>
        <DataBlocks.Block>
          <NewErrorBlock />
        </DataBlocks.Block>
      </DataBlocks.Container>
    );
  }

  return (
    <Switch>
      <Route
        path={indexUrl}
        exact
        render={() => (
          <DataBlocks.Container>
            <DataBlocks.Block>
              <Heading title="Детализация">
                <SectionWrapper isFetching={isAnyFetching} skeletonName="controls">
                  <div className={cx('button-wrapper')}>
                    {isRegularDetalizationReport && (
                      <div className={cx('button')}>
                        <Button
                          variant="tertiary"
                          size="s"
                          onClick={onOpenRegularDetalizationReportModal}
                          fullWidth={isMobile}
                        >
                          Регулярный отчёт
                        </Button>
                      </div>
                    )}
                    <div className={cx('button')}>
                      <Button
                        variant="secondary"
                        size="s"
                        onClick={onOpenDetalizationReportModal}
                        fullWidth={isMobile}
                      >
                        Заказать детализацию
                      </Button>
                    </div>
                  </div>
                </SectionWrapper>
              </Heading>
              <CtnBlock />
              <div className={cx('dropdown-wrapper')}>
                <div className={cx('dropdown-item')}>
                  {(activeTabs === 'debit' && callDetails?.length !== 0 && dropDownList.length > 2) && (
                    <Dropdown
                      options={dropDownList}
                      value={selectedDropdown}
                      onChange={handleChangeFilterDebitBalance}
                    />
                  )}
                </div>
              </div>
              <AccumulatorPage />
              <div className={cx('skeleton-wrapper')}>
                <SectionWrapper
                  isFetching={isAnyFetching}
                  skeletonName="table"
                >
                  <NewTable {...dataTable} />
                </SectionWrapper>
              </div>
            </DataBlocks.Block>
          </DataBlocks.Container>
        )}
      />
      <Route
        path={dataUrl}
        render={() => (
          <ExpensePage
            title={EXPENSE_PAGE.data.title}
            text={EXPENSE_PAGE.data.text}
            date={EXPENSE_PAGE.data.date}
          />
        )}
      />
      <Route
        path={callUrl}
        render={() => (
          <ExpensePage
            title={EXPENSE_PAGE.call.title}
            text={EXPENSE_PAGE.call.text}
            date={EXPENSE_PAGE.call.date}
          />
        )}
      />
      <Route
        path={smsUrl}
        render={() => (
          <ExpensePage
            title={EXPENSE_PAGE.sms.title}
            text={EXPENSE_PAGE.sms.text}
            date={EXPENSE_PAGE.sms.date}
          />
        )}
      />
      <Route
        path={smsUrlRoaming}
        render={() => (
          <ExpensePage
            title={EXPENSE_PAGE.smsRoaming.title}
            text={EXPENSE_PAGE.smsRoaming.text}
            date={EXPENSE_PAGE.sms.date}
          />
        )}
      />
      <Route
        path={callUrlRoaming}
        render={() => (
          <ExpensePage
            title={EXPENSE_PAGE.callRoaming.title}
            text={EXPENSE_PAGE.callRoaming.text}
            date={EXPENSE_PAGE.callRoaming.date}
          />
        )}
      />
      <Route
        path={dataUrlRoaming}
        render={() => (
          <ExpensePage
            title={EXPENSE_PAGE.dataRoaming.title}
            text={EXPENSE_PAGE.dataRoaming.text}
            date={EXPENSE_PAGE.dataRoaming.date}
          />
        )}
      />
      <Redirect to={indexUrl} />
    </Switch>
  );
};

export default PrepaidDetalization;
